/* eslint-disable react-hooks/rules-of-hooks */
import { Grid, LoadingDots, Section } from '@flos/react-ui';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useOutletContext, useParams, useSearchParams } from 'react-router-dom';

import { FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';

import { ConfigContext } from '../context/ConfigContext';
import { SubmitContext } from '../context/SubmitContext';
import { checkConfigIntegrity } from '../helpers/checkConfigIntegrity';
import { getPageConfFromPageId } from '../helpers/getPageConfFromPageId';
import { getPageIdFirst } from '../helpers/getPageIdFirst';
import { isFormApiEnabled } from '../helpers/isFormApiEnabled';
import { getFormConfigFromApi } from '../services/claimFormApi';
import useAppState from '../state/appState';
import { RouteableFC } from '../types/react';
import {
    trackABHighwaySplitTest,
    trackDidTheUserGoThroughTheHighway,
    trackFormInit,
    trackLeaveBeforeSubmission
} from '../utils/tracking';
import FormNavigation from './FormNavigation';
import Header from './Header';
import LoadSavedData from './LoadSavedData';
import Progressbar from './Progressbar';
import { NarrowGridItem, WideGridItem } from './layout/grids';

type FormProps = {
    pageId: undefined | string;
    skipLoadPrompt: boolean;
    isDeeplink: boolean;
    loadSource: string;
    isFromHighwayPath?: boolean;
};

type OutletContext = {
    setIsPageValid: Dispatch<SetStateAction<boolean>>;
};

const Form: RouteableFC<FormProps> = ({ pageId, loadSource, skipLoadPrompt, isDeeplink, isFromHighwayPath }) => {
    const formConfig = useAppState((state) => state.formConfig);

    const topConfig = useContext(ConfigContext);
    const { isSubmitting } = useContext(SubmitContext);

    const [search] = useSearchParams();
    const isFromDeeplink = search.get('deeplink') === 'true';

    useEffect(() => {
        if (formConfig) {
            trackFormInit(formConfig, '');
            trackABHighwaySplitTest(formConfig, '');
            trackDidTheUserGoThroughTheHighway(formConfig, '');
        }
    }, [formConfig]);

    useEffect(() => {
        if (!formConfig) {
            return;
        }
        if (!pageId) {
            return;
        }

        const actionsBeforeLeaving = () => trackLeaveBeforeSubmission(formConfig, pageId);

        window.addEventListener('beforeunload', actionsBeforeLeaving);

        // If the user is submitting, there is no longer a need to track customers leaving prematurely.
        // After submitting, the customer is redirected to the receipt page, and that
        // would otherwise trigger the premature-leave tracking

        if (isSubmitting) {
            window.removeEventListener('beforeunload', actionsBeforeLeaving);
        }
        return () => window.removeEventListener('beforeunload', actionsBeforeLeaving);
    }, [formConfig, pageId, isSubmitting, topConfig]);

    if (!formConfig || !pageId) {
        return null;
    }

    const pageConfig = getPageConfFromPageId(formConfig, pageId);

    if (!pageConfig) {
        throw new Error('No pageConfig with the supplied pageId: ' + pageId);
    }

    const showProgressBar = pageConfig.showProgressBar;

    const isLoggedOn = topConfig.user.isLoggedOn;
    const shouldLoadSavedData = isLoggedOn && formConfig.enableSave;

    const OutletGridItem = pageConfig.hasWideContent ? WideGridItem : NarrowGridItem;

    return (
        <div className="wrapper">
            {shouldLoadSavedData && (
                <LoadSavedData
                    loadSource={loadSource}
                    skipLoadPrompt={skipLoadPrompt}
                    isDeeplink={isDeeplink}
                    isFromHighwayPath={isFromHighwayPath}
                />
            )}
            <Header pageId={pageId} />
            <Section noSpaceAbove>
                <Grid gap={2}>
                    {/* UX wishes the progress bar and buttons to remain at the flos "narrow" Grid position
                        and if the content needs more space indicated by pageConfig.hasWideContent 
                        we only make the content wider */}
                    <NarrowGridItem>{showProgressBar && <Progressbar pageId={pageId} />}</NarrowGridItem>
                    <OutletGridItem>
                        <Outlet />
                    </OutletGridItem>
                    {!isFromDeeplink && (
                        <OutletGridItem>
                            <FormNavigation pageId={pageId} />
                        </OutletGridItem>
                    )}
                </Grid>
            </Section>
        </div>
    );
};

export const useFormOutlet = () => useOutletContext<OutletContext>();

// Bootstrap the form
Form.InitFromRoute = () => {
    const { formId, pageId } = useParams();
    const location = useLocation();
    const [search] = useSearchParams();

    // user's saved forms are displayed as a list on claims overview
    // clicking a "draft" there navigates here with some search params
    const shouldSkipLoadPrompt = search.get('skipLoadPrompt') === 'true';
    const loadSource = search.get('loadSource') ?? 'highway';

    // If user selected form from highway path the form should be deleted
    const isFromHighwayPath = search.get('fromHighwayPath') === 'true';

    // is this a deeplink
    const isDeeplink = search.get('deeplink') === 'true';

    const [formIdIsValid, setFormIdIsValid] = useState<boolean>(true);

    const formConfig = useAppState((state) => state.formConfig);
    const resumeFromSave = useAppState((state) => state.resumeFromSave);

    const resetFormConfig = useAppState((state) => state.resetFormConfig);
    const resetFormValues = useAppState((state) => state.resetFormValues);
    const setFormConfig = useAppState((state) => state.setFormConfig);
    const setFormId = useAppState((state) => state.setFormId);
    const resetResumedFromSave = useAppState((state) => state.resetResumedFromSave);

    const topConfig = useContext(ConfigContext);

    const loadFormConfigFromApi = useCallback(
        async (formId: string) => {
            const formConfigFromApi = await getFormConfigFromApi(formId);
            checkConfigIntegrity(formConfigFromApi);
            setFormConfig(formConfigFromApi);
        },
        [setFormConfig]
    );

    useEffect(() => {
        resetFormConfig();
        resetResumedFromSave();

        // Due to some issue with hot reload from the development env
        // on UJL's machine the reset form will only run if the value is 1
        // which is default
        if (process.env.REACT_APP_RESET_FORM_VALUES === '1') {
            resetFormValues();
        }

        setFormId(formId);

        // Should this form be loaded from the Claim Form API, or from a local file (embedded in the build artifacts)?
        if (formId && isFormApiEnabled(formId)) {
            loadFormConfigFromApi(formId);
        } else {
            // If the formId is a www-form, we need to import the form config from the open folder
            const pathPrefix = formId?.match(/-www$/) ? 'open/' : '';

            // Load the form config
            (async () => {
                try {
                    const module = await import(`../config/forms/${pathPrefix}${formId}`);

                    if (!module.default) {
                        throw new Error(`Missing .default() for: ../config/forms/${pathPrefix}${formId}`);
                    }

                    const config = module.default as FormConfig;

                    checkConfigIntegrity(config);
                    setFormConfig(config);
                } catch (error) {
                    console.error(error);
                    // @TODO: Add tracking
                    setFormIdIsValid(false);
                }
            })();
        }
    }, [
        formId,
        setFormId,
        setFormIdIsValid,
        setFormConfig,
        resetFormConfig,
        resetFormValues,
        loadFormConfigFromApi,
        resetResumedFromSave
    ]);

    const isRunningOnLocalhost = window.location.href.indexOf('localhost') > -1;

    if (!formId) {
        console.error(`Form.InitFromRoute: No formId - Navigating to ${topConfig.highwayUrl}`);

        // Don't redirect to the highway if we are in localhost (we want devs to be able to see the console errors)
        if (!isRunningOnLocalhost) {
            window.location.href = topConfig.highwayUrl;
        }
    }

    if (!formIdIsValid) {
        console.error(`Form.InitFromRoute: Invalid formId - Navigating to ${topConfig.highwayUrl}`);

        // Don't redirect to the highway if we are in localhost (we want devs to be able to see the console errors)
        if (!isRunningOnLocalhost) {
            window.location.href = topConfig.highwayUrl;
        }
    }

    // FormConfig is not yet loaded - Initial rendering
    // The useEffect will run later
    if (formConfig === undefined) {
        return <LoadingDots />;
    }

    // If the user types a bogus URL, navigate him to the first page
    //
    // This also happens when they change forms
    // When that happens, the previous* formFonfig is in the state
    // The pageId from the URL is being looked up in this obsolete formConfig
    // It cannot be found, and then the user is redirected
    // to the first page of the *obsolete* form!
    if (formConfig.formId !== formId) {
        // YIJ
        // - I forget why returning LoadingDots actually helps?
        //   Because we are waiting for the new formConfig
        //   to be loaded and set in state?
        return <LoadingDots />;
    }

    const firstPageId = getPageIdFirst(formConfig);
    const retainParams = {
        pathname: firstPageId,
        search: location.search,
        hash: location.hash
    };

    if (!pageId) {
        return <Navigate to={retainParams} />;
    }

    // The pageid does not exist in the current formConfig
    // Redirect them to the first page of the form
    // discarding the pageId they provided in the URL
    const pageConfig = getPageConfFromPageId(formConfig, pageId);
    if (!pageConfig) {
        return <Navigate to={retainParams} />;
    }

    if (resumeFromSave.isResumed) {
        // hash for hippo survey
        window.location.hash = 'fortsat';
    }

    return (
        <Form
            pageId={pageId}
            loadSource={loadSource}
            skipLoadPrompt={shouldSkipLoadPrompt}
            isDeeplink={isDeeplink}
            isFromHighwayPath={isFromHighwayPath}
        />
    );
};

export default Form;
